import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { profileService } from '@/services/profile';
import { setProfile } from '@/store/slices/authSlice';
import { profileKeys } from './queryKeys';
import { toast } from 'sonner';
import type { UpdateProfileInput, AvatarUploadOptions } from '@/services/profile/types';

export const useProfileMutations = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const updateMyProfile = useMutation({
    mutationFn: (input: UpdateProfileInput) => profileService.updateMyProfile(input),
    onSuccess: (updatedProfile) => {
      // Update React Query cache
      queryClient.setQueryData(profileKeys.myProfile(), updatedProfile);
      
      // Update Redux store for immediate UI updates
      dispatch(setProfile(updatedProfile));
      
      // Show success message
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to update profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });

  const updateUserProfile = useMutation({
    mutationFn: ({ userId, input, profileId }: { userId: string; input: UpdateProfileInput; profileId?: string }) => {
      return profileService.updateUserProfile(userId, input);
    },
    onSuccess: (updatedProfile, { userId, profileId }) => {
      // Update React Query cache
      queryClient.setQueryData(profileKeys.userProfile(userId), updatedProfile);
      
      // Invalidate team member profile cache using the correct profile ID
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', profileId] });
      }
      // Also invalidate with userId as fallback
      queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', userId] });
      
      // Invalidate team members list to reflect changes
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
      
      // Show success message
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to update profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: ({ 
      file, 
      userId, 
      companyId, 
      profileId,
      options 
    }: { 
      file: File; 
      userId: string; 
      companyId: string; 
      profileId?: string;
      options?: AvatarUploadOptions;
    }) => profileService.processAvatarUpload(file, userId, companyId, profileId, options),
    onSuccess: (result, { userId, profileId }) => {
      // Show success message
      toast.success('Profile picture uploaded successfully');
      
      // Invalidate profile queries to refetch with new avatar
      queryClient.invalidateQueries({ queryKey: profileKeys.myProfile() });
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: profileKeys.userProfile(profileId) });
      }
      
      // Invalidate team members list to reflect avatar changes
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
    },
    onError: (error: Error) => {
      console.error('Failed to upload avatar:', error);
      toast.error(`Failed to upload profile picture: ${error.message}`);
    },
  });

  const deleteAvatar = useMutation({
    mutationFn: (filePath: string) => profileService.deleteAvatar(filePath),
    onSuccess: () => {
      toast.success('Profile picture removed successfully');
      
      // Invalidate profile queries to refetch without avatar
      queryClient.invalidateQueries({ queryKey: profileKeys.myProfile() });
    },
    onError: (error: Error) => {
      console.error('Failed to delete avatar:', error);
      toast.error(`Failed to remove profile picture: ${error.message}`);
    },
  });

  return {
    updateMyProfile,
    updateUserProfile,
    uploadAvatar,
    deleteAvatar,
  };
};
